import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiLoader,
  FiInbox,
  FiClock,
  FiFileText,
  FiCheckCircle,
  FiAward, // Marks ke liye icon
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

const QuizManagement = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState([]);

  // 1. Initial State mein 'marks' add kiya
  const initialFormState = {
    title: "",
    exam: "",
    category: "",
    isFree: true,
    totalQuestions: 1,
    durationMinutes: 30,
    marks: "", // Naya field
    status: "active",
  };

  const [formData, setFormData] = useState(initialFormState);

  const fetchQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth("post", "/admin/getQuizzes", {});
      if (res?.code === 0) {
        setQuizzes(res.data || []);
      } else {
        showErrorToast(res?.message || "Failed to load quizzes");
      }
    } catch (error) {
      showErrorToast("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/getQuizCategories", {});
      if (res?.code === 0) {
        setCategories(res.data || []);
      }
    } catch (err) {
      showErrorToast("Category fetch error");
    }
  }, []);

  useEffect(() => {
    fetchQuizzes();
    fetchCategories();
  }, [fetchQuizzes, fetchCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      // 2. Data ko number mein convert karke bhej rahe hain
      const payload = {
        ...formData,
        totalQuestions: Number(formData.totalQuestions),
        durationMinutes: Number(formData.durationMinutes),
        marks: Number(formData.marks),
      };

      if (editingId) {
        res = await makeRequestAuth("post", "/admin/updateQuiz", {
          ...payload,
          id: editingId,
        });
      } else {
        res = await makeRequestAuth("post", "/admin/addQuiz", payload);
      }

      if (res?.code === 0) {
        showSuccessToast(editingId ? "Quiz updated" : "Quiz added");
        closeModal();
        fetchQuizzes();
      } else {
        showErrorToast(res?.message || "Operation failed");
      }
    } catch (error) {
      showErrorToast("Something went wrong");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quiz?")) return;
    try {
      const res = await makeRequestAuth("post", "/admin/deleteQuiz", { id });
      if (res?.code === 0) {
        showSuccessToast("Quiz deleted");
        fetchQuizzes();
      }
    } catch (error) {
      showErrorToast(error.message || "Unable to delete quiz");
    }
  };

  const openEditModal = (quiz) => {
    setEditingId(quiz._id);
    setFormData({
      title: quiz.title,
      exam: quiz.exam,
      category: quiz.category,
      isFree: quiz.isFree,
      totalQuestions: quiz.totalQuestions,
      durationMinutes: quiz.durationMinutes,
      marks: quiz.marks || "", // Edit ke liye marks load karein
      status: quiz.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  return (
    <div className="p-4">
      <ToastContainer />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Quiz Management
          </h1>
          <p className="text-sm text-slate-500">
            Create and monitor your quiz exams
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-[#2563eb] text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 shadow-md transition-all active:scale-95 font-medium"
        >
          <FiPlus size={20} /> Add New Quiz
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <FiLoader className="animate-spin text-blue-600 mb-4" size={40} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quizzes.map((quiz) => (
            <div
              key={quiz._id}
              className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <span
                  className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${quiz.isFree ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}
                >
                  {quiz.isFree ? "Free" : "Paid"}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(quiz)}
                    className="text-slate-400 hover:text-blue-600"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(quiz._id)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
              <h3 className="font-bold text-slate-800 text-lg mb-1 leading-tight">
                {quiz.title}
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                {quiz.exam} • {quiz.category}
              </p>

              <div className="grid grid-cols-3 gap-2 border-t pt-4 border-slate-50">
                <div className="flex items-center gap-1 text-slate-600">
                  <FiFileText size={12} />{" "}
                  <span className="text-[10px] font-semibold">
                    {quiz.totalQuestions} Qs
                  </span>
                </div>
                <div className="flex items-center gap-1 text-slate-600">
                  <FiClock size={12} />{" "}
                  <span className="text-[10px] font-semibold">
                    {quiz.durationMinutes} Min
                  </span>
                </div>
                {/* Card mein Marks display */}
                <div className="flex items-center gap-1 text-slate-600">
                  <FiAward size={12} />{" "}
                  <span className="text-[10px] font-semibold">
                    {quiz.marks} Marks
                  </span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span
                  className={`text-[10px] font-bold flex items-center gap-1 ${quiz.status === "active" ? "text-blue-600" : "text-slate-400"}`}
                >
                  <FiCheckCircle /> {quiz.status.toUpperCase()}
                </span>
                <button
                  onClick={() =>
                    (window.location.href = `/quiz-questions/${quiz._id}`)
                  }
                  className="text-[11px] bg-slate-800 text-white px-3 py-1.5 rounded-lg hover:bg-black transition-all font-semibold"
                >
                  Manage Questions
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? "Edit Quiz" : "Create New Quiz"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 overflow-y-auto max-h-[80vh]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Quiz Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Exam Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.exam}
                    onChange={(e) =>
                      setFormData({ ...formData, exam: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Category
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) =>
                      cat.subCategories?.map((sub, idx) => (
                        <option key={`${cat._id}-${idx}`} value={sub.name}>
                          {cat.name} → {sub.name}
                        </option>
                      )),
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Total Questions
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.totalQuestions}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        totalQuestions: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Duration (Mins)
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.durationMinutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        durationMinutes: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 3. Naya Marks Input Field */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Total Marks
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.marks}
                    onChange={(e) =>
                      setFormData({ ...formData, marks: e.target.value })
                    }
                    placeholder="e.g. 100"
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    id="isFree"
                    checked={formData.isFree}
                    onChange={(e) =>
                      setFormData({ ...formData, isFree: e.target.checked })
                    }
                    className="w-5 h-5 accent-blue-600"
                  />
                  <label
                    htmlFor="isFree"
                    className="text-sm font-bold text-slate-700"
                  >
                    This is a Free Quiz
                  </label>
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
                >
                  {editingId ? "Update Quiz" : "Save Quiz"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizManagement;
